import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _styled, { default as _styled2 } from "styled-components";
var _templateObject;
var _excluded = ["size", "open", "onDismiss", "onEscape", "unmountChildrenOnClose", "hideCloseBtn", "ignoreEscape", "ignoreBackdropClick", "hideBackdrop", "backdropWithBlur", "hideDialogSheet", "rootClassName", "className", "backdropClassName", "disabledScrollLock", "disabledFocusLock", "children", "containerHTMLProps", "isDark", "dark", "datatestid", "data-testid", "htmlProps"];
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import cn from 'classnames';
import CloseSmall from '@crehana/icons/outline/CloseSmall';
import { useMountChildrenValidation } from '@crehana/react-hooks';
import { Col, Container, Row } from "../GridV2";
import { KEY_NAMES } from "../helpers/constants";
import parseClassnames from "../helpers/parseClassnames";
import useWindowInnerHeight from "../hooks/useWindowInnerHeight";
import { baseLighter } from "../styles/colors";
import { DIALOG_SHEET_COL_PROPS } from "./constants";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Dialog = function Dialog(_ref) {
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'm' : _ref$size,
    _ref$open = _ref.open,
    open = _ref$open === void 0 ? false : _ref$open,
    onDismiss = _ref.onDismiss,
    onEscape = _ref.onEscape,
    _ref$unmountChildrenO = _ref.unmountChildrenOnClose,
    unmountChildrenOnClose = _ref$unmountChildrenO === void 0 ? true : _ref$unmountChildrenO,
    _ref$hideCloseBtn = _ref.hideCloseBtn,
    hideCloseBtn = _ref$hideCloseBtn === void 0 ? false : _ref$hideCloseBtn,
    _ref$ignoreEscape = _ref.ignoreEscape,
    ignoreEscape = _ref$ignoreEscape === void 0 ? false : _ref$ignoreEscape,
    _ref$ignoreBackdropCl = _ref.ignoreBackdropClick,
    ignoreBackdropClick = _ref$ignoreBackdropCl === void 0 ? false : _ref$ignoreBackdropCl,
    _ref$hideBackdrop = _ref.hideBackdrop,
    hideBackdrop = _ref$hideBackdrop === void 0 ? false : _ref$hideBackdrop,
    _ref$backdropWithBlur = _ref.backdropWithBlur,
    backdropWithBlur = _ref$backdropWithBlur === void 0 ? false : _ref$backdropWithBlur,
    _ref$hideDialogSheet = _ref.hideDialogSheet,
    hideDialogSheet = _ref$hideDialogSheet === void 0 ? false : _ref$hideDialogSheet,
    _ref$rootClassName = _ref.rootClassName,
    rootClassName = _ref$rootClassName === void 0 ? '' : _ref$rootClassName,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$backdropClassNam = _ref.backdropClassName,
    backdropClassName = _ref$backdropClassNam === void 0 ? '' : _ref$backdropClassNam,
    disabledScrollLock = _ref.disabledScrollLock,
    disabledFocusLock = _ref.disabledFocusLock,
    children = _ref.children,
    _ref$containerHTMLPro = _ref.containerHTMLProps,
    containerHTMLProps = _ref$containerHTMLPro === void 0 ? {} : _ref$containerHTMLPro,
    isDark = _ref.isDark,
    dark = _ref.dark,
    datatestid = _ref.datatestid,
    dataTestId = _ref['data-testid'],
    htmlProps = _ref.htmlProps,
    dialogProps = _objectWithoutProperties(_ref, _excluded);
  var windowInnerHeight = useWindowInnerHeight();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isClient = _useState2[0],
    setIsClient = _useState2[1];
  var dialogSheetRef = useRef(null);
  var shouldMountChildren = useMountChildrenValidation({
    isOpen: open,
    unmountChildrenOnClose: unmountChildrenOnClose
  });
  useEffect(function () {
    setIsClient(true);
  }, []);
  useEffect(function () {
    var clickOutsideListener = function clickOutsideListener(event) {
      if (dialogSheetRef.current && !dialogSheetRef.current.contains(event.target) && onDismiss) {
        onDismiss();
      }
    };
    if (open && !ignoreBackdropClick && size !== 'full') {
      document.addEventListener('mousedown', clickOutsideListener);
      document.addEventListener('touchstart', clickOutsideListener);
    } else {
      document.removeEventListener('mousedown', clickOutsideListener);
      document.removeEventListener('touchstart', clickOutsideListener);
    }
    return function () {
      document.removeEventListener('mousedown', clickOutsideListener);
      document.removeEventListener('touchstart', clickOutsideListener);
    };
  }, [ignoreBackdropClick, onDismiss, open, size]);
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === KEY_NAMES.ESCAPE) {
      event.stopPropagation();
      if (typeof onEscape === 'function') {
        onEscape(event);
      }
      if (open && !ignoreEscape && typeof onDismiss === 'function') {
        onDismiss();
      }
    }
  };

  // SSR support
  if (!isClient || typeof document === 'undefined') {
    return null;
  }
  var renderCloseButton = function renderCloseButton() {
    var buttonClasses = 'mr-20 mt-40 sm:mr-24 sm:mt-24';
    var iconClasses = 'w-24 h-24 sm:w-32 sm:h-32';
    if (size === 's') {
      buttonClasses = 'mr-8 mt-8 sm:mr-16 sm:mt-16';
      iconClasses = 'w-24 h-24';
    }
    if (size === 'full') {
      buttonClasses = 'mr-16 mt-12 sm:mt-32 sm:mr-32';
      iconClasses += ' lg:w-48 lg:h-48';
    }
    return /*#__PURE__*/_jsx("button", {
      type: "button",
      className: "focus:outline-none absolute top-0 right-0 z-dialog ".concat(buttonClasses),
      onClick: onDismiss,
      "data-testid": "close-dialog",
      children: /*#__PURE__*/_jsx(CloseSmall, {
        className: iconClasses,
        color: baseLighter
      })
    });
  };
  var renderChildren = function renderChildren() {
    return shouldMountChildren ? children : null;
  };
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({
    className: parseClassnames(rootClassName, cn('fixed inset-0 z-dialog', open ? 'cui-is-visible' : 'cui-is-hidden')),
    role: "dialog"
  }, containerHTMLProps), {}, {
    children: [!hideBackdrop && /*#__PURE__*/_jsx(_StyledDiv, {
      className: parseClassnames(backdropClassName, cn('fixed inset-0 -z-1', {
        'bg-base-main-80': !backdropWithBlur || isDark,
        'bg-base-lighter-56': backdropWithBlur && !isDark
      })),
      tabIndex: -1,
      $_css: backdropWithBlur ? "\n                backdrop-filter: blur(4px);\n                -webkit-backdrop-filter: blur(4px);\n              " : undefined
    }), /*#__PURE__*/_jsx(FocusLock, {
      disabled: disabledFocusLock || open,
      returnFocus: true,
      className: "h-full",
      children: /*#__PURE__*/_jsx(RemoveScroll, {
        enabled: !disabledScrollLock && open,
        forwardProps: true,
        children: /*#__PURE__*/_jsx(_StyledDiv2, {
          className: "absolute top-0 left-0 right-0 ",
          $_css2: windowInnerHeight,
          children: hideDialogSheet ? renderChildren() : /*#__PURE__*/_jsx(Container, {
            className: "relative items-center h-full z-1 focus:outline-none",
            full: size === 'full' || {
              xs: size !== 's',
              sm: false
            },
            ignoreKeyboardDebug: size === 'full',
            "data-testid": dataTestId || datatestid,
            htmlProps: {
              role: 'document',
              tabIndex: -1,
              onKeyDown: handleKeyDown
            },
            children: /*#__PURE__*/_jsx(Row, {
              className: "items-center h-full",
              nowrap: true,
              children: /*#__PURE__*/_jsx(Col, _objectSpread(_objectSpread({}, DIALOG_SHEET_COL_PROPS[size]), {}, {
                children: /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({
                  ref: dialogSheetRef,
                  className: parseClassnames(className, cn('relative overflow-y-auto overflow-x-hidden', isDark || dark ? 'bg-base-dark text-white' : 'bg-white text-base-main', size === 's' ? 'max-h-90vh' : 'h-full max-h-screen', {
                    'rounded-5': size !== 'full',
                    'sm:h-auto sm:min-h-0 sm:max-h-90vh': size !== 'full'
                  }))
                }, dialogProps), htmlProps), {}, {
                  style: _objectSpread({
                    height: size === 'full' ? windowInnerHeight : ''
                  }, dialogProps.style),
                  children: [!hideCloseBtn && renderCloseButton(), renderChildren()]
                }))
              }))
            })
          })
        })
      })
    })]
  })), document.body);
};
export default Dialog;
var _StyledDiv = /*#__PURE__*/_styled("div")(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteral(["", ""])), function (p) {
  return p.$_css;
});
var _StyledDiv2 = /*#__PURE__*/_styled2("div").withConfig({
  componentId: "sc-1ft8bnx-0"
})(["\n              transform: translateY(0);\n              animation: height ease 0.2s;\n              height: ", "px;\n            "], function (p) {
  return p.$_css2;
});